import { call, all, put, takeLatest, select, delay } from 'redux-saga/effects';

import { saveAs } from 'file-saver';

import ratingsService from './ratings-service';
import notification from '~/services/notification';

import { ISA_ERROR, USER_ROLE } from '~/constants';
import { GET_ALL_SELECTS_REQUEST, GET_ALL_REQUEST, SHARE_CSV } from './ratings-constants';

import * as ratingsActions from './ratings-actions';

import { formatMessage } from '~/i18n/translate';

export function* getAll({ filters }: any) {
  try {
    const { page, rowsPerPage, order } = yield select(state => state.ratings.list);

    const { externalCode } = yield select(state => state.auth.user);

    yield put(ratingsActions.setLoadingData(true));

    const params = {
      userExternalCode: filters?.vendor ?? '',
      clientExternalCode: filters?.client ?? '',
      startDate: filters?.date
        ? filters?.date[0]?.toISOString()
        : new Date(new Date().toISOString().split('T')[0] + 'T00:00:00'),
      endDate: filters?.date
        ? filters?.date[1]?.toISOString()
        : new Date(new Date().toISOString().split('T')[0] + 'T00:00:00'),
      orderAsc: order === 'asc' ?? 'asc'
    };

    const [{ data: sellers }, { data: ratings }, { data: rowsCount }, { data: clients }] = yield all([
      call(ratingsService.getSellers),
      call(ratingsService.getAllRatings, {
        skip: page === 0 ? 0 : page * rowsPerPage,
        limit: rowsPerPage,
        ...params
      }),
      call(ratingsService.getRowCount, {
        ...params
      }),
      call(ratingsService.getClients, {
        userExternalCode: externalCode,
        queryString: ''
      })
    ]);

    const parsedData = ratings?.map(item => ({
      ...item,
      vendor: sellers?.find(seller => seller?.externalCode === item?.userExternalCode)
    }));

    yield put(
      ratingsActions.getAllSuccess({
        rows: parsedData,
        selects: {
          vendors: sellers?.filter(seller => seller?.userRole === USER_ROLE?.VENDOR),
          clients
        },
        rowsCount
      })
    );

    yield put(ratingsActions.setLoadingData(false));
  } catch (error) {
    yield put(ratingsActions.setLoadingData(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* getAllSelects({ client }: any) {
  yield delay(200);
  try {
    yield put(ratingsActions.setLoadingSelects(true));

    const { externalCode } = yield select(state => state.auth.user);

    const [{ data: sellers }, { data: clients }] = yield all([
      call(ratingsService.getSellers),
      call(ratingsService.getClients, {
        userExternalCode: externalCode,
        queryString: client ?? ''
      })
    ]);

    yield put(
      ratingsActions.getAllSelectsSuccess({
        selects: {
          vendors: sellers?.filter(seller => seller?.userRole === USER_ROLE?.VENDOR),
          clients
        }
      })
    );
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
    yield put(ratingsActions.setLoadingSelects(false));
  } finally {
    yield put(ratingsActions.setLoadingSelects(false));
  }
}

export function* shareCsv({ filters }: any) {
  const toast: any = notification.loading(formatMessage('commonLoadingMessage'));

  try {
    const params = {
      userExternalCode: filters?.vendor?.externalCode ?? '',
      clientExternalCode: filters?.client?.externalCode ?? '',
      startDate: filters?.date
        ? filters?.date[0]?.toISOString()
        : new Date(new Date().toISOString().split('T')[0] + 'T00:00:00'),
      endDate: filters?.date
        ? filters?.date[1]?.toISOString()
        : new Date(new Date().toISOString().split('T')[0] + 'T00:00:00')
    };

    const { data: csv } = yield call(ratingsService.getCsv, params);

    const reader = new FileReader();

    const blobFile = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

    reader.readAsDataURL(blobFile);
    reader.onloadend = async function () {
      saveAs(blobFile, `Avaliações.csv`);
    };
  } catch (error) {
    yield put(ratingsActions.setLoadingData(false));
    yield put({ type: ISA_ERROR, error });
  } finally {
    toast.close();
  }
}

export default all([
  takeLatest(GET_ALL_REQUEST, getAll),
  takeLatest(SHARE_CSV, shareCsv),
  takeLatest(GET_ALL_SELECTS_REQUEST, getAllSelects)
]);
